import { Mail, MapPin, MessageCircle, Phone, Youtube } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { SITE } from "../data/site";
import { PRODUCTS } from "../data/products";

export default function Contact() {
    const mapsQuery = encodeURIComponent(
        "Morning Star Enterprises, Vylassery Road, Palarivattom, Kochi 682025",
    );
    const mapsLink = `https://www.google.com/maps/search/?api=1&query=${mapsQuery}`;
    const mapsEmbed = `https://www.google.com/maps?q=${mapsQuery}&output=embed`;
    return (
        <>
            <Seo
                title="Contact Morningstar Enterprises Kochi | Phone & WhatsApp"
                description="Contact Morningstar Enterprises, Vylassery Road, Palarivattom, Kochi. Call or WhatsApp +91 94471 77779 for LED boards, video walls & AV solutions in Kerala."
                path="/contact"
                keywords="contact Morningstar Enterprises Kochi, LED sign board makers in Ernakulam, scrolling message display suppliers Palarivattom, audio-visual institutional suppliers Kochi, LED board dealer Palarivattom, Morningstar phone number, LED display WhatsApp Kerala"
            />

            <section className="led-dots spotlight border-b border-slate-800/70" data-testid="contact-hero">
                <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24">
                    <p className="font-led text-lg uppercase tracking-[0.35em] text-[#00A8FF]">
                        Contact &amp; Support
                    </p>
                    <h1 className="mt-4 max-w-3xl font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl">
                        Talk to a real engineer, <span className="led-glow-amber">today.</span>
                    </h1>
                    <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400 md:text-lg">
                        No forms, no queues. Call or WhatsApp us with your site photo and
                        dimensions — we reply with technical guidance and a commercial quote.
                    </p>
                    <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-500">
                        Morningstar Enterprises are LED sign board makers in Ernakulam and
                        scrolling message display suppliers in Palarivattom, and among the most
                        experienced audio-visual institutional suppliers in Kochi — serving every
                        district of Kerala including Thrissur, Kozhikode, Thiruvananthapuram,
                        Kollam, Kottayam, Alappuzha, Palakkad, Malappuram, Kannur and Kasaragod,
                        as well as Tamil Nadu and Karnataka.
                    </p>
                </div>
            </section>

            <section className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2" data-testid="contact-details">
                <div className="space-y-6">
                    <Reveal>
                        <div className="rounded-2xl border border-slate-800 bg-[#12141E] p-6">
                            <h2 className="font-display text-xl font-semibold uppercase tracking-wide">
                                Visit us
                            </h2>
                            <a
                                href={mapsLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="contact-address-link"
                                className="mt-3 flex gap-3 text-base not-italic leading-relaxed text-slate-400 transition-colors hover:text-[#FF9900]"
                            >
                                <MapPin className="mt-1 h-5 w-5 shrink-0 text-[#FF9900]" aria-hidden="true" />
                                {SITE.address}
                            </a>
                            <p className="mt-3 text-sm text-slate-500">
                                Serving {SITE.regions}
                            </p>
                            <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#FF9900]">
                                Tap the address to open in Google Maps
                            </span>
                        </div>
                    </Reveal>
                    <Reveal delay={0.08}>
                        <div className="rounded-2xl border border-slate-800 bg-[#12141E] p-6">
                            <h2 className="font-display text-xl font-semibold uppercase tracking-wide">
                                Call or WhatsApp
                            </h2>
                            <a
                                href={SITE.phoneTel}
                                data-testid="contact-phone-link"
                                className="mt-3 flex items-center gap-3 text-lg font-semibold text-slate-100 transition-colors hover:text-[#FF9900]"
                            >
                                <Phone className="h-5 w-5 text-[#FF9900]" aria-hidden="true" />
                                {SITE.phoneDisplay}
                            </a>
                            <a
                                href={SITE.whatsapp}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="contact-whatsapp-cta"
                                className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 font-semibold text-black shadow-[0_0_18px_rgba(37,211,102,0.45)] transition-transform hover:scale-105"
                            >
                                <MessageCircle className="h-5 w-5" /> Start WhatsApp Chat
                            </a>
                            <div className="mt-5 border-t border-slate-800 pt-4">
                                <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                                    Regional call lines
                                </p>
                                <ul className="mt-3 space-y-2 text-sm text-slate-300">
                                    <li className="flex flex-wrap items-center gap-2">
                                        Kochi (Ernakulam) &amp; Thrissur:
                                        <a href="tel:+919447177779" data-testid="contact-line-kochi" className="font-semibold text-[#FF9900]">
                                            +91 94471 77779
                                        </a>
                                    </li>
                                    <li className="flex flex-wrap items-center gap-2">
                                        Calicut (Kozhikode) &amp; Trivandrum (Thiruvananthapuram):
                                        <a href="tel:+919349177779" data-testid="contact-line-calicut" className="font-semibold text-[#FF9900]">
                                            +91 93491 77779
                                        </a>
                                    </li>
                                </ul>
                                <p className="mt-2 text-xs text-slate-500">
                                    WhatsApp for all locations: +91 94471 77779
                                </p>
                            </div>
                        </div>
                    </Reveal>
                    <Reveal delay={0.16}>
                        <div className="rounded-2xl border border-slate-800 bg-[#12141E] p-6">
                            <h2 className="font-display text-xl font-semibold uppercase tracking-wide">
                                Email
                            </h2>
                            <ul className="mt-3 space-y-2">
                                {SITE.emails.map((e) => (
                                    <li key={e}>
                                        <a
                                            href={`mailto:${e}`}
                                            data-testid={`contact-email-${e.split("@")[0]}`}
                                            className="flex items-center gap-3 text-base text-slate-300 transition-colors hover:text-[#FF9900]"
                                        >
                                            <Mail className="h-5 w-5 text-[#FF9900]" aria-hidden="true" />
                                            {e}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                            <a
                                href={SITE.youtube}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="contact-youtube-link"
                                className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-slate-300 transition-colors hover:text-[#FF2D2D]"
                            >
                                <Youtube className="h-5 w-5 text-[#FF2D2D]" aria-hidden="true" />
                                Watch installation demos on YouTube
                            </a>
                        </div>
                    </Reveal>
                </div>
                <Reveal delay={0.1}>
                    <div className="h-full">
                        <div className="h-80 overflow-hidden rounded-2xl border border-slate-800 lg:h-[26rem]">
                            <iframe
                                title="Morningstar Enterprises location map — Vylassery Road, Palarivattom, Kochi"
                                src={mapsEmbed}
                                loading="lazy"
                                className="h-full w-full grayscale-[35%]"
                                data-testid="contact-map"
                            />
                        </div>
                        <a
                            href={mapsLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="contact-directions-cta"
                            className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#FF9900] px-6 py-3 font-semibold text-black transition-transform hover:scale-105"
                        >
                            <MapPin className="h-5 w-5" aria-hidden="true" /> Get Directions on Google Maps
                        </a>
                    </div>
                </Reveal>
            </section>

            <section className="border-t border-slate-800/70 bg-[#0C0E16]" data-testid="contact-products">
                <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
                    <Reveal>
                        <h2 className="font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl">
                            What would you like to discuss?
                        </h2>
                        <p className="mt-2 text-base text-slate-400">
                            Pick a product — your WhatsApp message starts pre-filled for it.
                        </p>
                    </Reveal>
                    <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {PRODUCTS.map((p, i) => (
                            <Reveal key={p.slug} delay={(i % 3) * 0.06}>
                                <a
                                    href={SITE.whatsappFor(p.name)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`contact-product-${p.slug}`}
                                    className="flex items-center justify-between gap-3 rounded-xl border border-slate-800 bg-[#12141E] px-5 py-4 transition-colors hover:border-[#25D366]/50"
                                >
                                    <span className="text-sm font-semibold text-slate-200">{p.name}</span>
                                    <MessageCircle className="h-4 w-4 shrink-0 text-[#25D366]" aria-hidden="true" />
                                </a>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}
